/**
 * useMessageDetailsPage
 *
 * Encapsulates all business logic for MessageDetailsPage:
 * - Data fetching (thread details + paginated replies)
 * - Permission computation
 * - WebSocket real-time reply injection
 * - Page-title side effect
 * - Auto-mark-as-read side effect
 * - User action handlers (mark unread, add self, load contacts)
 *
 * The page component remains a pure layout/render shell.
 */

import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';

import { useMessageContext } from '../context';
import { useThreadDetailsAndReplies } from './useMessageDetails';
import { useUpdateReadPointer, useInviteUsers } from './useMessageMutations';
import { getClientUserList } from '../services/messageService';
import { messageQueryKeys } from '../config/queryKeys';

import { useUserDetailsContext } from 'context/userDetailsContext';
import { useWebSocket } from 'context/WebSocketConnectionContext';
import { useToast } from 'context/ToastContext';
import { useNavbarContext } from 'context/NavbarContext';
import { toastConstant } from 'constants/toastmessage';
import { handleRedirection, setAppTitle } from 'utils/utils';
import { generateFullName } from 'utils/generateFullNameUtils';

/**
 * @returns {{
 *   messageDetails: object|null,
 *   threadDetails: object|null,
 *   totalCount: number,
 *   isLoadingDetails: boolean,
 *   isFetchingNextPage: boolean,
 *   hasNextPage: boolean,
 *   fetchNextPage: Function,
 *   refetchReplies: Function,
 *   refetchAll: Function,
 *   isErrorDetails: boolean,
 *   errorDetails: Error|null,
 *   hasPermission: boolean,
 *   showInviteDialog: boolean,
 *   setShowInviteDialog: Function,
 *   caseUserClients: Array,
 *   isFromClientPortal: boolean,
 *   isFromLead: boolean,
 *   isGlobal: boolean,
 *   handleMarkAsUnRead: Function,
 *   handleAddSelf: Function,
 *   loadCaseContacts: Function,
 * }}
 */
export function useMessageDetailsPage() {
  const { message_id } = useParams();
  const { addToast } = useToast();
  const { subscribe } = useWebSocket();
  const history = useHistory();
  const userContext = useUserDetailsContext();
  const queryClient = useQueryClient();

  const { isFromClientPortal, isFromLead, isGlobal } = useMessageContext();
  const navbarContextData = useNavbarContext();

  // Stable primitives — used in dep arrays instead of the whole context object
  const firmUserId = userContext?.firmDetails?.firm_user_id;
  const clientCognitoUsername = userContext?.firmDetails?.client_cognito_username;
  const contactId = userContext?.firmDetails?.contact_id;
  const userFirmUserId = userContext?.userDetails?.firm_user_id;
  const userContactId = userContext?.userDetails?.contact_id;

  // ── Queries ───────────────────────────────────────────────────────────────
  const {
    threadDetails,
    replies,
    totalCount,
    isLoadingDetails,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    refetchReplies,
    refetchAll,
    isErrorDetails,
    errorDetails,
  } = useThreadDetailsAndReplies(message_id, { pageSize: 20 });

  // Merges thread metadata with replies for backward-compat with child components
  const messageDetails = useMemo(() => {
    if (!threadDetails) return null;
    return { ...threadDetails, message: replies };
  }, [threadDetails, replies]);

  // ── Mutations ─────────────────────────────────────────────────────────────
  const updateReadPointerMutation = useUpdateReadPointer();
  const inviteUsersMutation = useInviteUsers();

  // ── Invite dialog state + contact list ───────────────────────────────────
  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const [caseUserClients, setCaseUserClients] = useState([]);

  // ── Permissions ───────────────────────────────────────────────────────────
  const hasPermission = useMemo(() => {
    const isContact = (threadDetails?.contacts || []).some((c) => c?.contact_id === contactId);
    const isFirmUser = (threadDetails?.firm_users || []).some((u) => u?.firm_user_id === firmUserId);
    const groupMembers = (threadDetails?.groups || []).flatMap((g) => g?.members || []);
    const isGroupMember = groupMembers.some((m) => m?.firm_user_id === firmUserId);
    return isContact || isFirmUser || isGroupMember;
  }, [threadDetails?.firm_users, threadDetails?.contacts, threadDetails?.groups, firmUserId, contactId]);

  // ── WebSocket: inject incoming replies directly into cache ────────────────
  useEffect(() => {
    if (!message_id || !subscribe) return;

    const handleSecureMessage = (data) => {
      if (data?.secure_message_type !== 'reply') return;

      const body = data?.body;
      if (!body) return;
      if (body?.message_thread_id !== message_id) return;

      const senderId = data?.created_by_user_id;
      if (senderId && senderId === clientCognitoUsername) return;

      const incomingReply = {
        message_thread_id: body.message_thread_id,
        case_id: body.case_id,
        case_name: body.case_name,
        subject: body.subject,
        message: body.message,
        created_at: body.created_at,
        created_by_name: body.created_by_name,
        sk: body.created_at,
        attachment: [],
        author_details: { first_name: data?.name || body.created_by_name },
        time: new Date().toISOString(),
        created_by_user_image: data?.created_by_user_image,
      };

      queryClient.setQueryData(messageQueryKeys.threadReplies.byId(message_id), (oldData) => {
        if (!oldData?.pages?.length) return oldData;

        const updatedPages = [...oldData.pages];
        updatedPages[0] = {
          ...updatedPages[0],
          data: {
            ...updatedPages[0].data,
            replies: [incomingReply, ...(updatedPages[0].data?.replies || [])],
            count: (updatedPages[0].data?.count || 0) + 1,
          },
        };
        return { ...oldData, pages: updatedPages };
      });

      // Mark the thread as read immediately after the new reply lands
      const userId = userFirmUserId || userContactId;
      if (userId) {
        updateReadPointerMutation.mutate({
          message_thread_id: message_id,
          user_id: userId,
          message_type: 'reply',
          unread: false,
          excludeFromLoading: true,
        });
      }
    };

    const unsubscribe = subscribe('secure_message', handleSecureMessage);
    return () => unsubscribe?.();
  }, [message_id, firmUserId, contactId, userFirmUserId, userContactId, subscribe]);

  // ── WebSocket: update read receipts (other_readers) in cache ─────────────
  useEffect(() => {
    if (!message_id || !subscribe) return;

    const handleReadStatus = (data) => {
      if (data?.message_thread_id !== message_id) return;

      const { reply_id, reader } = data;
      if (!reply_id || !reader?.user_id) return;

      queryClient.setQueryData(messageQueryKeys.threadReplies.byId(message_id), (oldData) => {
        if (!oldData?.pages?.length) return oldData;

        const updatedPages = oldData.pages.map((page) => ({
          ...page,
          data: {
            ...page.data,
            replies: (page.data?.replies || []).map((reply) => {
              // Remove this reader from every reply first (they may have previously
              // been the last reader of a different reply in the same thread)
              const readersWithoutUser = (reply.other_readers || []).filter((r) => r.user_id !== reader.user_id);

              const isTargetReply = reply.sk === reply_id || reply.sk?.includes(reply_id);

              if (isTargetReply) {
                // Append the reader to this reply's other_readers
                return { ...reply, other_readers: [...readersWithoutUser, reader] };
              }

              // Return unchanged if no readers were removed; otherwise return updated
              if (readersWithoutUser.length === (reply.other_readers || []).length) return reply;
              return { ...reply, other_readers: readersWithoutUser };
            }),
          },
        }));

        return { ...oldData, pages: updatedPages };
      });
    };

    const unsubscribe = subscribe('message_read_status', handleReadStatus);
    return () => unsubscribe?.();
  }, [message_id, subscribe]);

  // ── Page title ────────────────────────────────────────────────────────────
  useEffect(() => {
    setAppTitle('Messages', threadDetails?.subject || '');
    return () => setAppTitle('Messages');
  }, [threadDetails?.subject]);

  // ── Auto-mark as read ─────────────────────────────────────────────────────
  // Ref guard prevents duplicate calls on re-renders for the same unread thread
  const markedReadForThreadRef = useRef(null);
  useEffect(() => {
    const threadId = threadDetails?.message_thread_id;
    if (!threadId || threadDetails?.is_read !== false) return;
    if (markedReadForThreadRef.current === threadId) return;

    const userId = userFirmUserId || userContactId;
    if (!userId) return;

    markedReadForThreadRef.current = threadId;
    updateReadPointerMutation.mutate(
      {
        message_thread_id: threadId,
        user_id: userId,
        message_type: 'reply',
        unread: false,
        excludeFromLoading: true,
      },
      {
        onSuccess: () => {
          navbarContextData?.notificationData?.loadNotificationCount();
        },
      }
    );
  }, [threadDetails?.message_thread_id, userFirmUserId, userContactId, hasPermission]);

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handleMarkAsUnRead = useCallback(
    async (e, details) => {
      e.preventDefault();

      if (details?.message_thread_id) {
        await updateReadPointerMutation.mutateAsync({
          message_thread_id: details.message_thread_id,
          user_id: userFirmUserId || userContactId,
          message_type: 'reply',
          unread: true,
        });
      }

      const url = isFromClientPortal
        ? '/client/messages'
        : isGlobal
        ? '/messages'
        : `/${isFromLead ? 'leads' : 'cases'}/${details?.case_id}/messages`;
      handleRedirection(history, url);
    },
    [userFirmUserId, userContactId, isFromClientPortal, isGlobal, isFromLead]
  );

  // Depends on threadDetails (not messageDetails) so it isn't recreated on
  // every new reply arriving via WebSocket or optimistic update.
  const handleAddSelf = useCallback(() => {
    const name = generateFullName(userContext?.userDetails);
    const {
      profile_image,
      client_cognito_username,
      is_confirmed,
      last_name,
      firm_user_id,
      access_level,
      first_name,
      status,
      calendar_color,
      email,
      pk,
    } = userContext?.userDetails || {};

    inviteUsersMutation.mutate(
      {
        message_thread_id: threadDetails?.message_thread_id,
        sk: `message#${threadDetails?.message_thread_id}`,
        isLead: threadDetails?.is_lead,
        data: {
          invited_user: [
            {
              profile_image,
              client_cognito_username,
              is_confirmed,
              last_name,
              firm_user_id,
              access_level,
              first_name,
              status,
              calendar_color,
              email,
              pk,
              recipient_name: name,
              fullName: name,
              recipient_id: client_cognito_username,
              add_my_self: true,
            },
          ],
          invited_group: [],
        },
      },
      {
        onSuccess: () => {
          refetchAll();
          addToast(false, toastConstant.toasterType.SUCCESS, toastConstant.api.SUCCESS, toastConstant.message.USER_ADDED_SUCCESS);
        },
        onError: () => {
          addToast(false, toastConstant.toasterType.ERROR, toastConstant.api.FAILED, toastConstant.message.USER_ADDED_FAILED);
        },
      }
    );
  }, [threadDetails?.message_thread_id, threadDetails?.is_lead, userContext?.userDetails]);

  const loadCaseContacts = useCallback(
    (caseId = '') => {
      getClientUserList({
        caseId,
        all_contacts: isGlobal,
        contact_id: isGlobal ? userContactId : false,
        is_team_member_list: isGlobal,
        is_include_groups: true,
        active_groups: true,
        excludeFromLoading: false,
      })
        .then((res) => setCaseUserClients(res?.data || []))
        .catch((err) => {
          console.error('Load case contacts error:', err);
          addToast(false, toastConstant.toasterType.ERROR, toastConstant.api.FAILED, 'Failed to load contacts');
        });
    },
    [isGlobal, userContactId]
  );

  // ── Derived navigation URL for error state ────────────────────────────────
  const backUrl = isFromClientPortal ? '/client/messages' : isGlobal ? '/messages' : `/${isFromLead ? 'leads' : 'cases'}/messages`;

  return {
    // Data
    messageDetails,
    threadDetails,
    totalCount,
    // Loading / error
    isLoadingDetails,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    refetchReplies,
    refetchAll,
    isErrorDetails,
    errorDetails,
    // Computed
    hasPermission,
    backUrl,
    // Context flags
    isFromClientPortal,
    isFromLead,
    isGlobal,
    history,
    // Invite dialog
    showInviteDialog,
    setShowInviteDialog,
    caseUserClients,
    // Handlers
    handleMarkAsUnRead,
    handleAddSelf,
    loadCaseContacts,
  };
}
